/**
 * @file src/pages/company-settings/index.jsx
 * @description Company settings page — tabbed interface for company
 * profile, ZATCA config, tax settings, and integrations.
 *
 * What changed from the original:
 *  - useGetCompany import path updated (typo fix: useGetCompnay → useGetCompany)
 *  - company?.company?.legalName → company?.legalName
 *    useGetCompany selects data.company so the value is already unwrapped.
 *    Double .company was a bug causing undefined in the page title.
 *  - companyError and refetchCompany removed — destructured but never used
 *  - isLoadingCompany kept — could be used for skeleton states in future
 *
 * Rules:
 *  - No API calls directly — useGetCompany handles data fetching
 *  - Tab content components handle their own data needs
 */

import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "components/AppIcon";
import { SidebarProvider, useSidebar } from "components/ui/Sidebar";
import Sidebar from "components/ui/Sidebar";
import CompanyProfileTab from "./components/CompanyProfileTab";
import ZatcaConfigTab from "./components/ZatcaConfigTab";
import TaxSettingsTab from "./components/TaxSettingsTab";
import IntegrationTab from "./components/IntegrationTab";
import { useGetCompany } from "./useGetCompany";

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------

const TABS = [
  {
    id: "profile",
    labelKey: "companySettings.tabs.profile.label",
    icon: "Building2",
    descriptionKey: "companySettings.tabs.profile.description",
  },
  {
    id: "zatca",
    labelKey: "companySettings.tabs.zatca.label",
    icon: "Shield",
    descriptionKey: "companySettings.tabs.zatca.description",
  },
  {
    id: "tax",
    labelKey: "companySettings.tabs.tax.label",
    icon: "Calculator",
    descriptionKey: "companySettings.tabs.tax.description",
  },
  {
    id: "integration",
    labelKey: "companySettings.tabs.integration.label",
    icon: "Link",
    descriptionKey: "companySettings.tabs.integration.description",
  },
];

// ---------------------------------------------------------------------------
// Tab content router
// ---------------------------------------------------------------------------

const TabContent = ({ activeTab }) => {
  switch (activeTab) {
    case "profile":
      return <CompanyProfileTab />;
    case "zatca":
      return <ZatcaConfigTab />;
    case "tax":
      return <TaxSettingsTab />;
    case "integration":
      return <IntegrationTab />;
    default:
      return <CompanyProfileTab />;
  }
};

// ---------------------------------------------------------------------------
// CompanySettingsContent
// ---------------------------------------------------------------------------

const CompanySettingsContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const [activeTab, setActiveTab] = useState("profile");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // company is already unwrapped by useGetCompany's select: data => data?.company
  const { data: company, isLoading: isLoadingCompany } = useGetCompany();

  const currentTab = TABS.find((tab) => tab.id === activeTab);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsMobileMenuOpen(false);
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <Helmet>
        <title>
          {`${t("companySettings.title")}${company?.legalName ? ` - ${company.legalName}` : ""}`}
        </title>
        <meta
          name="description"
          content={t("companySettings.metaDescription")}
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* ── Header + Tabs ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {t("companySettings.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("companySettings.description")}
              </p>
            </div>

            {/* Mobile tab selector */}
            <div className="md:hidden relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-muted rounded-xl text-sm font-medium text-foreground"
                aria-expanded={isMobileMenuOpen}
                aria-haspopup="listbox"
              >
                <Icon name={currentTab?.icon} size={16} />
                <span>{t(currentTab?.labelKey)}</span>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`transition-transform ${isMobileMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Mobile dropdown */}
              <AnimatePresence>
                {isMobileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute end-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-30"
                    role="listbox"
                  >
                    <div className="p-2 space-y-1">
                      {TABS.map((tab, index) => (
                        <motion.button
                          key={tab.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          onClick={() => handleTabChange(tab.id)}
                          role="option"
                          aria-selected={activeTab === tab.id}
                          className={`w-full flex items-center gap-3 px-3 py-3 text-start rounded-lg transition-colors ${
                            activeTab === tab.id
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          <Icon name={tab.icon} size={16} />
                          <div>
                            <p className="text-sm font-medium">
                              {t(tab.labelKey)}
                            </p>
                            <p className="text-xs opacity-80">
                              {t(tab.descriptionKey)}
                            </p>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Desktop tabs */}
          <div
            className="hidden md:flex items-center gap-1 mt-6"
            role="tablist"
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-micro hover-lift ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground shadow-hero-cta"
                    : "text-muted-foreground hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* ── Mobile tab description ───────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="md:hidden mb-6 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center gap-3">
            <Icon name={currentTab?.icon} size={20} className="text-primary" />
            <div>
              <h2 className="text-lg font-medium text-foreground">
                {t(currentTab?.labelKey)}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(currentTab?.descriptionKey)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* ── Tab content ──────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              role="tabpanel"
            >
              <TabContent activeTab={activeTab} />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Mobile backdrop */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/20 z-20 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CompanySettings (with Sidebar)
// ---------------------------------------------------------------------------

const CompanySettings = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <CompanySettingsContent />
    </div>
  </SidebarProvider>
);

export default CompanySettings;
